import { useState } from 'react';
import { useLocation } from 'wouter';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Check, CreditCard, ArrowLeft } from 'lucide-react';
import { CheckoutForm } from '@/components/payment/CheckoutForm';
import { usePaymentMethods, useSubscriptions, useInvoices, useTransactions } from '@/hooks/usePayment';
import { PaymentMethodCard } from '@/components/payment/PaymentMethodCard';

const PRO_FEATURES = [
  'Premium Daily Tips (5+)',
  'In-Depth Match Analysis',
  'Bankroll Management Tools',
  'Priority Support',
  'All Free Tier Features',
];

export default function Subscribe() {
  const [, setLocation] = useLocation();
  const [showCheckout, setShowCheckout] = useState(false);

  const { data: methodsData } = usePaymentMethods();
  const { data: subscriptionsData } = useSubscriptions();
  const { data: invoicesData } = useInvoices();
  const { data: transactionsData } = useTransactions();

  const activeSubscription = subscriptionsData?.subscriptions?.find(
    (s) => s.status === 'active' || s.status === 'trialing'
  );

  if (showCheckout) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-20 pb-10 px-4 sm:px-6">
          <div className="max-w-xl mx-auto">
            <Button variant="ghost" className="mb-4" onClick={() => setShowCheckout(false)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <CheckoutForm onComplete={() => setLocation('/account')} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-20 pb-10 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold">Payment Management</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Manage your subscription and payment methods</p>
          </div>

          <Tabs defaultValue="subscription" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="methods">Payment Methods</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="subscription" className="space-y-4">
              {activeSubscription ? (
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-base">Active Subscription</CardTitle>
                    <CardDescription className="text-xs">Your current plan details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Status</span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
                        {activeSubscription.status}
                      </span>
                    </div>
                    {activeSubscription.currentPeriodEnd && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Current Period Ends</span>
                        <span className="text-sm font-medium">
                          {new Date(activeSubscription.currentPeriodEnd).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {activeSubscription.trialEnd && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Trial Ends</span>
                        <span className="text-sm font-medium">
                          {new Date(activeSubscription.trialEnd).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowCheckout(true)}
                    >
                      Change Plan
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-base">Upgrade to Pro</CardTitle>
                    <CardDescription className="text-xs">Get access to premium features</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Pro Plan</span>
                      <span className="text-xl font-bold text-primary">$29<span className="text-xs text-muted-foreground">/month</span></span>
                    </div>
                    <Separator />
                    <ul className="space-y-2">
                      {PRO_FEATURES.map((feature) => (
                        <li key={feature} className="text-xs sm:text-sm flex items-center gap-2">
                          <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full" onClick={() => setShowCheckout(true)}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Subscribe Now
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="methods" className="space-y-4">
              <Card className="glass">
                <CardHeader>
                  <CardTitle className="text-base">Saved Payment Methods</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {methodsData?.paymentMethods?.length ? (
                    methodsData.paymentMethods.map((method) => (
                      <PaymentMethodCard
                        key={method.id}
                        method={method}
                        onDelete={() => {}}
                        onSetDefault={() => {}}
                      />
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No saved payment methods</p>
                  )}
                  <Button variant="outline" className="w-full" onClick={() => setShowCheckout(true)}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Add Payment Method
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="space-y-4">
              <Card className="glass">
                <CardHeader>
                  <CardTitle className="text-base">Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  {invoicesData?.invoices?.length ? (
                    <div className="space-y-2">
                      {invoicesData.invoices.slice(0, 10).map((invoice) => (
                        <div key={invoice.id} className="flex justify-between items-center p-2 rounded bg-muted/50">
                          <div>
                            <p className="text-sm font-medium">{invoice.description || 'Invoice'}</p>
                            <p className="text-xs text-muted-foreground">{new Date(invoice.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">${(invoice.amountCents / 100).toFixed(2)}</p>
                            <p className="text-xs capitalize text-muted-foreground">{invoice.status}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No invoices yet</p>
                  )}
                </CardContent>
              </Card>

              <Card className="glass">
                <CardHeader>
                  <CardTitle className="text-base">Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  {transactionsData?.transactions?.length ? (
                    <div className="space-y-2">
                      {transactionsData.transactions.slice(0, 10).map((tx) => (
                        <div key={tx.id} className="flex justify-between items-center p-2 rounded bg-muted/50">
                          <div>
                            <p className="text-sm font-medium capitalize">{tx.type}</p>
                            <p className="text-xs text-muted-foreground">{new Date(tx.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">${(tx.amountCents / 100).toFixed(2)}</p>
                            <p className="text-xs capitalize text-muted-foreground">{tx.status}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No transactions yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
